import { QueryFunctionContext, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { useLocalization } from 'localization';
import notifyHandler from 'utils/notifyHandler';
import { datahubAxiosClient, DOWNLOAD_MATERIAL_ENDPOINT, ENDPOINT_DETAILS } from 'constants/server/apiEndPoint';
import { ICommonResponse } from 'api/types';
import { ApiMutationFn } from 'types/apiParamsTypes';
import { IDocumentMaterialDto, ILinkMaterialDto } from './dtos';
import { IDocumentMaterial, ILinkMaterial } from './models';
import { mapLinkOrDocumentMaterialFromDto } from './helpers';
import MaterialApi from '.';
import { ProductCategory } from 'api/packages/models';

export const materialQueryKey = {
  all: ['all'] as const,
  downloadMaterial: () => [...materialQueryKey.all, 'downloadMaterial'] as const,
  linkMaterials: (caseId: string) => [...materialQueryKey.all, caseId, 'linkMaterials'] as const,
  documentMaterials: (caseId: string) => [...materialQueryKey.all, caseId, 'documentMaterials'] as const,
  copyMaterialByCaseApi: (caseId: string) => [...materialQueryKey.all, caseId, 'copyMaterialByCaseApi'] as const,
};

type MaterialPayload = Partial<{
  caseId: string;
  materialIds: string[];
  email: string;
  webResolution: boolean;
  caseIds: string[];
  enabledQuery: boolean;
}> &
  ApiMutationFn;

export const downloadMaterialMutation = (payload: MaterialPayload) => {
  const { t } = useLocalization();
  const { caseId, materialIds, email, webResolution, handleSuccess } = payload;
  const { mutateAsync, isSuccess, isPending: isLoading } = useMutation({
    mutationFn: async () => {
      const response = await datahubAxiosClient.post<ICommonResponse>(DOWNLOAD_MATERIAL_ENDPOINT, {
        caseId,
        materialIds,
        email,
        webResolution,
      });
      return response;
    },
    onSuccess: res => {
      if (res?.data?.success) {
        notifyHandler({ type: 'success', content: t('common.terms.success') });
        handleSuccess?.();
      } else {
        notifyHandler({ type: 'error', content: t('materials.messages.packageZipFileFailed') });
      }
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('materials.messages.packageZipFileFailed') });
    },
  });

  return { mutateAsync, isSuccess, isLoading };
};

export const archiveMaterialMutation = (payload: MaterialPayload) => {
  const { t } = useLocalization();
  const { caseId, handleSuccess } = payload;
  const { mutateAsync, isSuccess, isPending: isLoading } = useMutation({
    mutationFn: async () => {
      const response = await datahubAxiosClient.post<ICommonResponse>(ENDPOINT_DETAILS.archiveMaterial(caseId ?? ''));
      return response;
    },
    onSuccess: res => {
      if (res?.data?.success) {
        notifyHandler({ type: 'success', content: t('modals.archiveMaterial.success') });
        handleSuccess?.();
      } else {
        notifyHandler({ type: 'error', content: t('modals.archiveMaterial.error.common') });
      }
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.archiveMaterial.error.common') });
    },
  });

  return { mutateAsync, isSuccess, isLoading };
};

export const getLinkMaterialsApi = (caseId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: materialQueryKey.linkMaterials(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get<ILinkMaterialDto[]>(ENDPOINT_DETAILS.linkMaterials(caseId), {
        signal,
      });
      return response?.data?.map<ILinkMaterial>(mapLinkOrDocumentMaterialFromDto) || [];
    },
    enabled: !!caseId,
  });

  return { linkMaterials: data, isLoadingLinkMaterial: isLoading };
};

export const getDocumentMaterialsApi = (caseId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: materialQueryKey.documentMaterials(caseId),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get<IDocumentMaterialDto[]>(
        ENDPOINT_DETAILS.documentMaterials(caseId),
        {
          signal,
        }
      );
      return response?.data?.map<IDocumentMaterial>(mapLinkOrDocumentMaterialFromDto) || [];
    },
    enabled: !!caseId,
  });

  return { documentMaterials: data, isLoadingDocumentMaterials: isLoading };
};

export const getListOfAssetsByCasesApi = (payload: MaterialPayload) => {
  const { caseIds = [] } = payload;
  const queries = useQueries({
    queries: caseIds.map(caseId => ({
      queryKey: materialQueryKey.copyMaterialByCaseApi(caseId ?? ''),
      queryFn: async ({ signal }: QueryFunctionContext) => {
        const response = await MaterialApi.getSimplifiedVisualMaterials(caseId, {
          signal,
        });
        return response.materials.filter(m => m.productCategory === ProductCategory.Location) || [];
      },
      enabled: caseIds.length > 0,
    })),
  });

  const isLoadingAssets = queries.some(q => q.isLoading);

  const caseMaterials = queries.map((q, idx) => ({
    caseId: caseIds[idx],
    visualMaterials: q.data ?? [],
  }));

  return { caseMaterials, isLoadingAssets };
};

export const saveDocumentAssetMutation = (payload: MaterialPayload) => {
  const { t } = useLocalization();
  const { caseId, handleSuccess } = payload;
  const queryClient = useQueryClient();
  const { mutateAsync, isSuccess, isPending: isLoading } = useMutation({
    mutationFn: MaterialApi.saveDocumentAsset,
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: materialQueryKey.documentMaterials(caseId ?? '') });
        notifyHandler({ type: 'success', content: t('modals.saveDocument.success') });
        handleSuccess?.();
      }
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.saveDocument.error') });
    },
  });

  return { saveDocumentAssetAsync: mutateAsync, isSuccess, savingDocuments: isLoading };
};

export const removeDocumentsMutation = (payload: MaterialPayload) => {
  const { t } = useLocalization();
  const { caseId, handleSuccess } = payload;
  const queryClient = useQueryClient();
  const { mutateAsync, isSuccess, isPending: isLoading } = useMutation({
    mutationFn: MaterialApi.deleteLinkAsset,
    onSuccess: response => {
      if (response.success) {
        notifyHandler({ type: 'success', content: t('materials.messages.linkRemoveSuccess') });
        handleSuccess?.();
      } else {
        notifyHandler({ type: 'error', content: t('materials.messages.linkRemoveFailed') });
      }
      queryClient.invalidateQueries({ queryKey: materialQueryKey.documentMaterials(caseId ?? '') });
      queryClient.invalidateQueries({ queryKey: materialQueryKey.linkMaterials(caseId ?? '') });
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('materials.messages.linkRemoveFailed') });
    },
  });

  return { removeDocumentAsync: mutateAsync, isSuccess, removingDocuments: isLoading };
};

export const saveLinkAssetMutation = (payload: MaterialPayload) => {
  const { t } = useLocalization();
  const { caseId, handleSuccess } = payload;
  const queryClient = useQueryClient();
  const { mutateAsync, isSuccess, isPending: isLoading } = useMutation({
    mutationFn: MaterialApi.saveLinkAsset,
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: materialQueryKey.linkMaterials(caseId ?? '') });
        notifyHandler({ type: 'success', content: t('modals.saveLink.success') });
        handleSuccess?.();
      } else {
        notifyHandler({ type: 'error', content: t('modals.saveLink.error') });
      }
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.saveLink.error') });
    },
  });

  return { saveLinkAssetAsync: mutateAsync, isSuccess, savingLink: isLoading };
};
